/**
 * Chat thread vs right rail:
 * - Chat: all assistant/user prose (any length), plus short status while working.
 * - Rail: artifacts only — ABAP source, report HTML/URL, security bodies, IS panel blocks (diagrams, tables).
 * Hide a bubble only when it duplicates artifact payload already on the rail (not because the reply is long).
 */

import {
  isSecurityDeliverable,
  looksLikeAbapSource,
  resolveDeliverableKind,
  resolveReportUrl,
} from '@/lib/chatDeliverable';

const SESSION_START_PREFIX = '[Session start]';
import { CHAT_STUDIO_NAME, CHAT_STUDIO_SUBTITLE, STUDIO_WELCOME_MESSAGE } from '@/lib/chatStudioBranding';

export { CHAT_STUDIO_NAME, CHAT_STUDIO_SUBTITLE, STUDIO_WELCOME_MESSAGE };

const PROCESSING_PLACEHOLDER = 'Processing your reply';

/** Generic in-flight label for the thread spinner (not intent-specific copy). */
export const CHAT_THINKING_LABEL = 'Thinking…';

/** Label beside the spinner while the agent is working. */
export function chatWorkingIndicatorLabel({ sending = false, generating = false, phase = '' } = {}) {
  const phaseUp = String(phase || '').toUpperCase();
  if (sending && !generating) return 'Sending…';
  if ((sending || generating) && phaseUp === 'DONE') return 'Sending…';
  if (sending || generating) return CHAT_THINKING_LABEL;
  return CHAT_THINKING_LABEL;
}

/**
 * Whether to show the in-thread "Assistant / Thinking…" row.
 * New sessions bootstrap gather welcome in the background (`generating: true`) while
 * the intro message is already visible — skip the spinner until the user sends a turn.
 */
export function shouldShowChatWorkingIndicator({
  sending = false,
  generating = false,
  hasUserMessages = false,
  phase = '',
} = {}) {
  if (sending) return true;
  if (!generating) return false;
  const phaseUp = String(phase || '').toUpperCase();
  if (!hasUserMessages && phaseUp === 'GATHER') return false;
  return true;
}

function isProcessingPlaceholder(text) {
  if (!text) return false;
  return text.replace(/[.\u2026…]/g, '').trim().toLowerCase() === PROCESSING_PLACEHOLDER.toLowerCase();
}

/** Bare ack after Connected-mode prompt (reply continue without retyping requirements). */
export function isContinueAcknowledgement(text) {
  const t = String(text || '').trim().toLowerCase();
  if (!t) return false;
  if (['continue', 'cont', 'go', 'ok', 'okay', 'yes', 'y', 'proceed', 'done', 'ready'].includes(t)) {
    return true;
  }
  return /^(continue|proceed|go ahead|let'?s continue)\b/.test(t);
}

/** Parse connected vs quick reply at the ABAP mode choice prompt. */
export function parseConnectedPreference(text) {
  const t = String(text || '').trim().toLowerCase();
  if (!t) return null;
  if (/\b(connected|studio|context|sap context|with context)\b/.test(t)) return true;
  if (/\b(quick|light|fast|without context|no context)\b/.test(t)) return false;
  return null;
}

/** Hide system rows and internal session-bootstrap text from the UI. */
export function isHiddenChatMessage(msg) {
  if (!msg) return true;
  const role = String(msg.role || '').toLowerCase();
  if (role === 'system') return true;
  const text = String(msg.content || '').trim();
  if (!text) return role !== 'user';
  if (text.startsWith(SESSION_START_PREFIX)) return true;
  if (
    role === 'assistant' &&
    isProcessingPlaceholder(text)
  ) {
    return true;
  }
  if (text.includes('opened a new chat session')) {
    return true;
  }
  if (
    role === 'assistant' &&
    /greet briefly and ask your first focused clarifying question/i.test(text)
  ) {
    return true;
  }
  return false;
}

export const ABAP_ARTIFACT_PLACEHOLDER =
  'ABAP generation complete. View and copy the program in the Generated ABAP panel on the right.';

export const SECURITY_ARTIFACT_PLACEHOLDER =
  'Security results are ready in the panel on the right.';

/** @deprecated use SECURITY_ARTIFACT_PLACEHOLDER */
export const SAP_AUTH_ARTIFACT_PLACEHOLDER = SECURITY_ARTIFACT_PLACEHOLDER;

export const STATIC_REPORT_ARTIFACT_PLACEHOLDER =
  'Static report ready. Open the report from the panel on the right.';

export const INTEGRATION_SUITE_ARTIFACT_PLACEHOLDER =
  'Integration Suite results are in the panel on the right.';

export const BASIS_SM21_ARTIFACT_PLACEHOLDER =
  'System log (SM21) results are in the panel on the right.';

function isArtifactBody(text, code, artifactUrl) {
  const t = String(text || '').trim();
  const c = String(code || '').trim();
  if (!t) return false;
  if (artifactUrl && t.includes(String(artifactUrl))) return true;
  if (!c) return false;
  if (t === c) return true;
  if (t.length > 200 && c.length > 200 && t.includes(c)) return true;
  return false;
}

function isDoneGateBoilerplate(text, kind) {
  const t = String(text || '').trim();
  if (!t) return false;
  if (isSecurityDeliverable(kind)) {
    return (
      t.startsWith('All set —') ||
      t.startsWith('Security request complete.') ||
      t.startsWith('Updated — check the panel') ||
      t.startsWith('Authorization analysis complete.') ||
      t.startsWith('Authorization analysis updated.')
    );
  }
  if (kind === 'static_report') {
    return t.startsWith('Static report ready.') || t.startsWith('Report updated.');
  }
  if (kind === 'integration_suite') {
    return (
      t.startsWith('Done — Integration Suite') ||
      t.startsWith('Updated — see the Integration Suite') ||
      t.startsWith('Integration Suite turn complete')
    );
  }
  if (kind === 'basis_sm21') {
    return (
      t.startsWith('SM21 analysis complete') ||
      t.startsWith('System log review complete') ||
      /panel on the right/i.test(t)
    );
  }
  return (
    t.startsWith('ABAP generation complete.') ||
    t.startsWith('ABAP code updated.') ||
    t.startsWith('Done — your ABAP') ||
    t.startsWith('Updated — the revised ABAP')
  );
}

/** In-flight ack shown while generate/revise runs in the background. */
export function isGenerateProgressMessage(text) {
  const t = String(text || '').trim();
  if (!t) return false;
  const normalized = t.replace(/[.\u2026…]/g, '').trim().toLowerCase();
  if (normalized === 'thinking') return true;
  return (
    t.startsWith('Generating ABAP (with SAP validation)') ||
    /^Generating ABAP[.\u2026…]*$/i.test(t) ||
    t.startsWith('Revising ABAP') ||
    t.startsWith('Generating ABAP from your approved PRD.') ||
    t.startsWith('Generating ABAP from your requirements') ||
    t.startsWith('On it — discovering OData, generating ABAP') ||
    t.startsWith('Discovering OData services, generating ABAP') ||
    t.startsWith('Fetching SAP sales data and building your static HTML report.') ||
    t.startsWith('Pulling SAP data and building your HTML report') ||
    t.startsWith('Running the Security Agent') ||
    t.startsWith('Running SAP authorization analysis.') ||
    t.startsWith('Applying your changes to the ABAP code') ||
    t.startsWith('Applying your changes (with SAP validation)') ||
    t.startsWith('Working on your Integration Suite request') ||
    t.startsWith('Integration Suite agent running') ||
    t.startsWith('Continuing Integration Suite conversation') ||
    t.startsWith('Reviewing the SAP system log') ||
    t.startsWith('Analyzing system log (SM21)') ||
    t.startsWith('Continuing SM21 analysis') ||
    /^Running (the )?Security Agent/i.test(t) ||
    /^Generating static report/i.test(t) ||
    /^Running New User Provisioning/i.test(t) ||
    /^Got it — building your/i.test(t)
  );
}

/** Assistant copy that should not appear in the thread while async work runs (spinner only). */
export function shouldHideAssistantWhileGenerating(content, opts = {}) {
  const t = String(content || '').trim();
  if (!t) return true;
  if (isProcessingPlaceholder(t)) return true;
  if (isGenerateProgressMessage(t)) return true;
  if (isPanelPlaceholderSummary(t, opts)) return true;
  return false;
}

/** Chat bubble / status copy that must not render as the right-rail deliverable body. */
export function isPanelPlaceholderSummary(text, opts = {}) {
  const t = String(text || '').trim();
  if (!t) return false;
  const intent = String(opts.intent || opts.artifactType || '').toLowerCase();
  if (isGenerateProgressMessage(t) || isProcessingPlaceholder(t)) return true;
  if (
    t === ABAP_ARTIFACT_PLACEHOLDER ||
    t === SECURITY_ARTIFACT_PLACEHOLDER ||
    t === STATIC_REPORT_ARTIFACT_PLACEHOLDER
  ) {
    return true;
  }
  if (isDoneGateBoilerplate(t, intent === 'sap_auth' ? 'security' : intent || 'abap')) {
    return true;
  }
  if (/panel on the right/i.test(t) && t.length < 320) return true;
  if (/^Processing your reply/i.test(t)) return true;
  return false;
}

export function sanitizeRailArtifactSummary(summary, opts = {}) {
  if (summary == null) return null;
  const s = String(summary).trim();
  if (!s) return null;
  return isPanelPlaceholderSummary(s, opts) ? null : s;
}

function assistantContentKey(text) {
  const t = String(text || '').trim();
  if (!t) return '';
  if (
    isDoneGateBoilerplate(t, 'abap') ||
    t === ABAP_ARTIFACT_PLACEHOLDER ||
    t.startsWith('ABAP code updated.')
  ) {
    return 'done:abap';
  }
  if (isDoneGateBoilerplate(t, 'security') || t === SECURITY_ARTIFACT_PLACEHOLDER) {
    return 'done:security';
  }
  if (isDoneGateBoilerplate(t, 'static_report') || t === STATIC_REPORT_ARTIFACT_PLACEHOLDER) {
    return 'done:static_report';
  }
  if (isGenerateProgressMessage(t)) return 'progress';
  return t;
}

/** Treat done-gate full text and UI placeholder as the same assistant bubble. */
export function assistantMessagesEquivalent(a, b) {
  const ka = assistantContentKey(a);
  const kb = assistantContentKey(b);
  if (!ka || !kb) return false;
  return ka === kb;
}

function hasEquivalentAssistantMessage(messages, content) {
  return (messages || []).some(
    (row) => row.role === 'assistant' && assistantMessagesEquivalent(row.content, content)
  );
}

function transcriptHasDoneMessage(messages) {
  return (messages || []).some((row) => {
    if (row.role !== 'assistant') return false;
    const key = assistantContentKey(row.content);
    return key.startsWith('done:');
  });
}

/** Index of the newest non-empty assistant row (for rail dedupe on the active turn only). */
export function indexOfLatestAssistantMessage(messages) {
  const list = messages || [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const row = list[i];
    if (row?.role === 'assistant' && String(row.content || '').trim()) {
      return i;
    }
  }
  return -1;
}

/**
 * Limit rail-vs-chat dedupe to the latest assistant bubble so earlier turns stay readable.
 */
export function chatMessageDisplayOpts(msg, messageIndex, messages, sessionOpts = {}) {
  const latestIdx = indexOfLatestAssistantMessage(messages);
  const isLatestAssistant = msg?.role === 'assistant' && messageIndex === latestIdx;
  if (isLatestAssistant) {
    return sessionOpts;
  }
  return {
    ...sessionOpts,
    railSummary: '',
    panelBlockCount: 0,
    railHasContent: false,
  };
}

/**
 * @param {string} content
 * @param {{ role?: string, generatedCode?: string | null, phase?: string, intent?: string, artifactType?: string, artifactUrl?: string | null }} opts
 */
export function displayChatMessageContent(content, opts = {}) {
  const role = (opts.role || 'assistant').toLowerCase();
  if (role === 'user') return content || '';

  const text = (content || '').trim();
  if (!text) return content || '';

  // Done-gate teasers are intent-specific; message text beats stale session intent.
  if (isDoneGateBoilerplate(text, 'abap') || text === ABAP_ARTIFACT_PLACEHOLDER) {
    return ABAP_ARTIFACT_PLACEHOLDER;
  }
  if (isDoneGateBoilerplate(text, 'static_report') || text === STATIC_REPORT_ARTIFACT_PLACEHOLDER) {
    return STATIC_REPORT_ARTIFACT_PLACEHOLDER;
  }
  if (isDoneGateBoilerplate(text, 'integration_suite')) {
    return INTEGRATION_SUITE_ARTIFACT_PLACEHOLDER;
  }
  if (isDoneGateBoilerplate(text, 'basis_sm21') || text === BASIS_SM21_ARTIFACT_PLACEHOLDER) {
    return BASIS_SM21_ARTIFACT_PLACEHOLDER;
  }
  if (isDoneGateBoilerplate(text, 'security') || text === SECURITY_ARTIFACT_PLACEHOLDER) {
    return SECURITY_ARTIFACT_PLACEHOLDER;
  }

  const code = (opts.generatedCode || '').trim();
  const kind = resolveDeliverableKind(opts.intent, opts.artifactType, code);
  const artifactUrl = resolveReportUrl(opts.artifactUrl, code || text);

  if (kind === 'security') {
    if (isArtifactBody(text, code, artifactUrl)) return SECURITY_ARTIFACT_PLACEHOLDER;
    if (isDoneGateBoilerplate(text, 'security')) return SECURITY_ARTIFACT_PLACEHOLDER;
    return content;
  }

  if (kind === 'static_report') {
    if (isArtifactBody(text, code, artifactUrl)) return STATIC_REPORT_ARTIFACT_PLACEHOLDER;
    if (isDoneGateBoilerplate(text, 'static_report')) return STATIC_REPORT_ARTIFACT_PLACEHOLDER;
    if (artifactUrl && text.includes(artifactUrl)) return STATIC_REPORT_ARTIFACT_PLACEHOLDER;
    return content;
  }

  if (kind === 'integration_suite') {
    if (text === INTEGRATION_SUITE_ARTIFACT_PLACEHOLDER) {
      return content;
    }
    const rail = String(opts.railSummary || '').trim();
    const hasRail =
      Number(opts.panelBlockCount || 0) > 0 ||
      Boolean(opts.railHasContent) ||
      rail.length > 80;
    if (hasRail && rail) {
      const t = text.trim();
      if (t === rail || t.startsWith(`${rail}\n`) || (t.length > 80 && t.startsWith(rail))) {
        return INTEGRATION_SUITE_ARTIFACT_PLACEHOLDER;
      }
    }
    return content;
  }

  if (kind === 'basis_sm21') {
    if (text === BASIS_SM21_ARTIFACT_PLACEHOLDER) {
      return content;
    }
    const rail = String(opts.railSummary || '').trim();
    const hasRail =
      Number(opts.panelBlockCount || 0) > 0 ||
      Boolean(opts.railHasContent) ||
      rail.length > 80;
    if (hasRail && rail) {
      const t = text.trim();
      if (t === rail || t.startsWith(`${rail}\n`) || (t.length > 80 && t.startsWith(rail))) {
        return BASIS_SM21_ARTIFACT_PLACEHOLDER;
      }
    }
    return content;
  }

  if (kind === 'abap') {
    if (isArtifactBody(text, code, artifactUrl)) return ABAP_ARTIFACT_PLACEHOLDER;
    if (isDoneGateBoilerplate(text, 'abap')) return ABAP_ARTIFACT_PLACEHOLDER;
    if (code && looksLikeAbapSource(text) && (text === code || text.includes(code))) {
      return ABAP_ARTIFACT_PLACEHOLDER;
    }
  }

  return content;
}

export function isStudioWelcomeMessage(content) {
  return String(content || '').trim() === STUDIO_WELCOME_MESSAGE;
}

/** Drop duplicate welcome replies once the user has sent a real turn. */
export function shouldIgnoreWelcomeAfterUserTurn(messages, content) {
  if (!isStudioWelcomeMessage(content)) return false;
  return (messages || []).some((m) => m.role === 'user' && String(m.content || '').trim());
}

/** Keep local user turns when poll/thread reload omits them from the API. */
function preserveOptimisticAssistants(prev, merged) {
  if (!prev?.length || !merged?.length) return merged;
  const lastUserInMerged = [...merged].reverse().find((row) => row.role === 'user');
  if (!lastUserInMerged) return merged;

  let prevUserIdx = -1;
  for (let i = prev.length - 1; i >= 0; i -= 1) {
    if (prev[i].role === 'user' && prev[i].content === lastUserInMerged.content) {
      prevUserIdx = i;
      break;
    }
  }
  if (prevUserIdx < 0) return merged;

  const out = [...merged];
  const mergedHasDone = transcriptHasDoneMessage(out);
  for (let i = prevUserIdx + 1; i < prev.length; i += 1) {
    const row = prev[i];
    if (row.role !== 'assistant') continue;
    const trimmed = String(row.content || '').trim();
    if (!trimmed || isProcessingPlaceholder(trimmed)) continue;
    if (hasEquivalentAssistantMessage(out, trimmed)) continue;
    if (isGenerateProgressMessage(trimmed)) continue;
    out.push(row);
  }
  return out;
}

export function mergePollThreadMessages(prev, incoming) {
  const visible = (incoming || []).filter((row) => !isHiddenChatMessage(row));
  if (!visible.length) return prev || [];

  const prevUsers = (prev || []).filter((row) => row.role === 'user');
  const hasAllUsers = prevUsers.every((user) =>
    visible.some((row) => row.role === 'user' && row.content === user.content)
  );
  if (hasAllUsers) {
    return preserveOptimisticAssistants(prev, visible);
  }

  const merged = [...visible];
  for (const user of prevUsers) {
    if (merged.some((row) => row.role === 'user' && row.content === user.content)) continue;
    let insertAt = merged.length;
    for (let i = merged.length - 1; i >= 0; i -= 1) {
      if (merged[i].role === 'assistant') {
        insertAt = i + 1;
        break;
      }
    }
    merged.splice(insertAt, 0, user);
  }
  return preserveOptimisticAssistants(prev, merged);
}

/**
 * Merge poll thread with local state, apply status text, and guarantee bootstrap welcome.
 */
export function finalizePollChatMessages(prev, incoming, { statusContent } = {}) {
  let next = mergePollThreadMessages(prev, incoming);
  const statusText = String(statusContent || '').trim();
  if (
    statusText &&
    !isProcessingPlaceholder(statusText) &&
    !isGenerateProgressMessage(statusText)
  ) {
    if (!hasEquivalentAssistantMessage(next, statusText)) {
      next = appendAssistantIfNew(next, statusText);
    }
  }
  const hasUser = next.some((row) => row.role === 'user');
  const hasAssistant = next.some(
    (row) => row.role === 'assistant' && String(row.content || '').trim()
  );
  if (!hasUser && !hasAssistant) {
    return [{ role: 'assistant', content: STUDIO_WELCOME_MESSAGE }];
  }
  return next;
}

/** Append assistant text unless duplicate welcome or identical last bubble. */
export function appendAssistantIfNew(prev, content) {
  const withoutPlaceholders = (prev || []).filter(
    (row) => !(row.role === 'assistant' && isProcessingPlaceholder(row.content))
  );
  if (!content) return withoutPlaceholders;
  if (shouldIgnoreWelcomeAfterUserTurn(withoutPlaceholders, content)) {
    return withoutPlaceholders;
  }
  if (hasEquivalentAssistantMessage(withoutPlaceholders, content)) {
    return withoutPlaceholders;
  }
  const last = withoutPlaceholders[withoutPlaceholders.length - 1];
  if (last?.role === 'assistant' && last.content === content) {
    return withoutPlaceholders;
  }
  return [...withoutPlaceholders, { role: 'assistant', content }];
}

/** Bounded scroll region for chat threads (flex child with internal overflow). */
export const CHAT_THREAD_SCROLL_CLASS =
  'min-h-[280px] max-h-[min(560px,calc(100vh-22rem))] flex-1 overflow-y-auto overscroll-contain';

/** True when the user is at (or near) the bottom of a scroll container. */
export function isChatScrollPinnedToBottom(element, threshold = 80) {
  if (!element) return true;
  return element.scrollHeight - element.scrollTop - element.clientHeight <= threshold;
}

export { isProcessingPlaceholder };
